import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Tabs,
    Tab,
    Spinner,
    DateRangePicker,
} from "@heroui/react";
import { LuPrinter } from "react-icons/lu";
import { parseDate } from "@internationalized/date";
import { useFetchData } from "@/hooks";
import {
    PageHeader,
    ThemedText,
    ErrorState,
    EmptyState,
} from "@/components/ui";
import { formatThousands, formatDecimal, formatDate } from "@/utils";

const TableReport = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedTab = searchParams.get("tab") || "today";
    const todayStr = new Date().toISOString().split("T")[0];
    const customRange = {
        start: parseDate(searchParams.get("start") || todayStr),
        end: parseDate(searchParams.get("end") || todayStr),
    };

    const updateParams = (updates) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            Object.entries(updates).forEach(([k, v]) => {
                if (v) next.set(k, v);
                else next.delete(k);
            });
            return next;
        });
    };

    const getReportParams = () => {
        if (selectedTab === "yesterday") {
            const date = new Date();
            date.setDate(date.getDate() - 1);
            return { date: date.toISOString().split("T")[0] };
        }
        if (selectedTab === "custom") {
            return {
                date_from: customRange.start.toString(),
                date_to: customRange.end.toString(),
            };
        }
        return {};
    };

    const { data, isLoading, error, refetch } = useFetchData(
        `report/daily-egg-production`,
        getReportParams(),
    );

    const reportData = Array.isArray(data) ? data : [];

    const displayDate =
        selectedTab === "custom"
            ? `${formatDate(customRange.start.toString())} s/d ${formatDate(customRange.end.toString())}`
            : selectedTab === "yesterday"
              ? formatDate(
                    new Date(new Date().setDate(new Date().getDate() - 1)),
                )
              : formatDate(new Date());

    const reportMeta = { display_date: displayDate };

    // Calculate Totals
    const totals = reportData.reduce(
        (acc, item) => {
            acc.mortality += Number(item.mortality || 0);
            acc.culling += Number(item.culling || 0);
            acc.population_remaining += Number(item.population_remaining || 0);
            acc.production_total += Number(item.production_total || 0);
            acc.bundles += Number(item.bundles || 0);
            acc.trays += Number(item.trays || 0);
            acc.pieces += Number(item.pieces || 0);
            acc.broken += Number(item.broken || 0);
            acc.production_accumulated += Number(
                item.production_accumulated || 0,
            );
            return acc;
        },
        {
            mortality: 0,
            culling: 0,
            population_remaining: 0,
            production_total: 0,
            bundles: 0,
            trays: 0,
            pieces: 0,
            broken: 0,
            production_accumulated: 0,
        },
    );

    // Calculate Average HD/HH
    const avgHD =
        reportData.length > 0
            ? reportData.reduce(
                  (sum, item) => sum + Number(item.hen_day || 0),
                  0,
              ) / reportData.length
            : 0;
    const avgHH =
        reportData.length > 0
            ? reportData.reduce(
                  (sum, item) => sum + Number(item.hen_house || 0),
                  0,
              ) / reportData.length
            : 0;

    if (error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Laporan"
                    description="Terjadi kesalahan saat memuat data laporan."
                    onAction={refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex items-center justify-between">
                <PageHeader
                    title="Laporan Produksi Harian Telur"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Laporan" },
                        { label: "Produksi Harian Telur" },
                    ]}
                />
                <Button
                    color="default"
                    size="sm"
                    startContent={<LuPrinter />}
                    isDisabled={isLoading || reportData.length === 0}
                    onPress={() => {
                        const p = getReportParams();
                        const qs = new URLSearchParams();
                        if (p.date) qs.set("date", p.date);
                        if (p.date_from) qs.set("date_from", p.date_from);
                        if (p.date_to) qs.set("date_to", p.date_to);
                        navigate(
                            `/daily-egg-production-report/print?${qs.toString()}`,
                        );
                    }}
                >
                    Print
                </Button>
            </div>

            <div className="flex items-center gap-4">
                <Tabs
                    aria-label="Date Filter"
                    selectedKey={selectedTab}
                    onSelectionChange={(key) => updateParams({ tab: key })}
                    variant="solid"
                    size="sm"
                >
                    <Tab key="today" title="Hari Ini" />
                    <Tab key="yesterday" title="Kemarin" />
                    <Tab key="custom" title="Custom" />
                </Tabs>
                {selectedTab === "custom" && (
                    <DateRangePicker
                        aria-label="Pilih Periode"
                        className="max-w-xs"
                        variant="flat"
                        size="sm"
                        value={customRange}
                        onChange={(range) =>
                            updateParams({
                                start: range.start.toString(),
                                end: range.end.toString(),
                            })
                        }
                    />
                )}
            </div>

            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-col items-start gap-0.5 px-4 pt-4">
                    <ThemedText size="md" className="font-semibold">
                        Ringkasan Produksi
                    </ThemedText>
                    <ThemedText size="xs" variant="caption">
                        Periode: {reportMeta.display_date || "-"}
                    </ThemedText>
                </CardHeader>
                <CardBody className="overflow-x-auto p-4 pt-2">
                    {isLoading ? (
                        <div className="flex h-48 w-full items-center justify-center">
                            <Spinner label="Memuat data..." color="primary" />
                        </div>
                    ) : reportData.length === 0 ? (
                        <EmptyState
                            title="Tidak Ada Data"
                            description="Belum ada data produksi untuk tanggal ini."
                        />
                    ) : (
                        <table className="themed-table">
                            <thead>
                                <tr>
                                    <th rowSpan={2}>Kandang</th>
                                    <th rowSpan={2}>Barak</th>
                                    <th rowSpan={2}>Umur (Hari)</th>
                                    <th colSpan={3}>Populasi Ayam</th>
                                    <th colSpan={2}>Pakan</th>
                                    <th colSpan={8}>Produksi</th>
                                </tr>
                                <tr>
                                    <th className="p-1 font-medium">Mati</th>
                                    <th className="p-1 font-medium">Afkir</th>
                                    <th className="p-1 font-medium">
                                        Ekor Hidup
                                    </th>
                                    <th className="p-1 font-medium">Jenis</th>
                                    <th className="p-1 font-medium">Goni</th>
                                    <th className="p-1 font-medium">Ikat</th>
                                    <th className="p-1 font-medium">Papan</th>
                                    <th className="p-1 font-medium">Butir</th>
                                    <th className="p-1 font-medium">Pecah</th>
                                    <th className="p-1 font-medium">Total</th>
                                    <th className="p-1 font-medium">
                                        Akumulasi
                                    </th>
                                    <th className="p-1 font-medium">HD</th>
                                    <th className="p-1 font-medium">HH</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.map((item, index) => (
                                    <tr
                                        key={index}
                                        className="hover:bg-default-50 transition-colors"
                                    >
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {item.barrack?.coop?.name ||
                                                    "-"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {item.barrack?.name || "-"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {item.age || "-"}
                                            </ThemedText>
                                        </td>

                                        <td className="text-center">
                                            <ThemedText
                                                size="xs"
                                                className="text-default-600"
                                            >
                                                {formatThousands(
                                                    item.mortality,
                                                )}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText
                                                size="xs"
                                                className="text-default-600"
                                            >
                                                {formatThousands(item.culling)}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {formatThousands(
                                                    item.population_remaining,
                                                )}
                                            </ThemedText>
                                        </td>

                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {item.feed_type?.name || "-"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {item.feed_zak
                                                    ? Number(item.feed_zak)
                                                    : "-"}
                                            </ThemedText>
                                        </td>

                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {formatThousands(item.bundles)}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {formatThousands(item.trays)}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {formatThousands(item.pieces)}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText
                                                size="xs"
                                                color="danger"
                                            >
                                                {formatThousands(item.broken)}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {formatThousands(
                                                    item.production_total,
                                                )}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText
                                                size="xs"
                                                className="text-default-600"
                                            >
                                                {formatThousands(
                                                    item.production_accumulated,
                                                )}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {formatDecimal(item.hen_day)}
                                            </ThemedText>
                                        </td>
                                        <td className="text-center">
                                            <ThemedText size="xs">
                                                {formatDecimal(item.hen_house)}
                                            </ThemedText>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={3} className="text-center">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            TOTAL
                                        </ThemedText>
                                    </td>
                                    <td className="text-center">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatThousands(totals.mortality)}
                                        </ThemedText>
                                    </td>
                                    <td className="text-center">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatThousands(totals.culling)}
                                        </ThemedText>
                                    </td>
                                    <td className="text-center">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatThousands(
                                                totals.population_remaining,
                                            )}
                                        </ThemedText>
                                    </td>
                                    <td className="text-center">-</td>
                                    <td className="text-center">-</td>
                                    <td className="text-center">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatThousands(totals.bundles)}
                                        </ThemedText>
                                    </td>
                                    <td className="text-center">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatThousands(totals.trays)}
                                        </ThemedText>
                                    </td>
                                    <td className="text-center">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatThousands(totals.pieces)}
                                        </ThemedText>
                                    </td>
                                    <td className="text-center">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatThousands(totals.broken)}
                                        </ThemedText>
                                    </td>
                                    <td className="text-center">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatThousands(
                                                totals.production_total,
                                            )}
                                        </ThemedText>
                                    </td>
                                    <td className="text-center">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatThousands(
                                                totals.production_accumulated,
                                            )}
                                        </ThemedText>
                                    </td>
                                    <td className="text-center">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatDecimal(avgHD)}
                                        </ThemedText>
                                    </td>
                                    <td className="text-center">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatDecimal(avgHH)}
                                        </ThemedText>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default TableReport;
